// =====================================================================
// Assessment Center — Instructor Panel
// Exams page — list all exams the instructor has created.
// =====================================================================
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  statusTone,
} from "../../../shared/components/ui";
import { listExams, type ExamWithDetail } from "../lib/mock-api";
import type { ExamStatus } from "../../../shared/types/db";

const examTypeLabel: Record<string, string> = {
  multiple_choice: "Multiple Choice",
  essay: "Essay",
  mixed: "Mixed",
};

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function ExamsPage() {
  const [exams, setExams] = useState<ExamWithDetail[]>([]);
  const [status, setStatus] = useState<ExamStatus | "">("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    listExams({ status: status || undefined }).then((data) => {
      setExams(data);
      setLoading(false);
    });
  }, [status]);

  return (
    <div>
      <PageHeader
        title="Exams"
        subtitle="Multiple choice, essay, and mixed exams you've built."
        action={
          <Link to="/instructor/assessment-center/exams/new">
            <Button>+ Create Exam</Button>
          </Link>
        }
      />

      <Card className="mb-5 flex items-center gap-3 p-4">
        <Select value={status} onChange={(e) => setStatus(e.target.value as ExamStatus | "")} className="max-w-[200px]">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="active">Active</option>
          <option value="closed">Closed</option>
          <option value="archived">Archived</option>
        </Select>
      </Card>

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-slate-500">
          <Spinner /> Loading exams…
        </div>
      ) : exams.length === 0 ? (
        <EmptyState
          title="No exams yet"
          description="Create your first exam by pulling questions from the question bank."
          action={
            <Link to="/instructor/assessment-center/exams/new">
              <Button>+ Create Exam</Button>
            </Link>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {exams.map((exam) => {
            const questionCount = exam.sections.reduce((n, s) => n + s.questions.length, 0);
            return (
              <Card key={exam.id} className="flex flex-col p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-base font-semibold text-navy-900">
                    {exam.title}
                  </h3>
                  <Badge tone={statusTone(exam.status)}>{exam.status}</Badge>
                </div>
                <p className="mt-1 text-xs text-slate-400">
                  {examTypeLabel[exam.exam_type]} · {exam.duration_minutes} min
                </p>
                {exam.description && (
                  <p className="mt-2 line-clamp-2 text-sm text-slate-500">{exam.description}</p>
                )}

                <dl className="mt-4 space-y-1 text-xs text-slate-500">
                  <div className="flex justify-between">
                    <dt>Sections</dt>
                    <dd>{exam.sections.length}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Questions</dt>
                    <dd>{questionCount}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Starts</dt>
                    <dd>{formatDate(exam.start_at)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Ends</dt>
                    <dd>{formatDate(exam.end_at)}</dd>
                  </div>
                </dl>

                <div className="mt-4 flex-1" />
                <Link to={`/instructor/assessment-center/exams/${exam.id}/edit`}>
                  <Button variant="secondary" className="w-full">
                    {exam.status === "draft" ? "Continue editing" : "View / Edit"}
                  </Button>
                </Link>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
